import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  UserCog, Plus, Edit, KeyRound, Shield, Search, Mail, IdCard,
  CheckCircle2, XCircle, Clock, Loader2, X, UserX, UserCheck, Users as UsersIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  adminListEmployees, adminCreateEmployee, adminUpdateEmployee,
  adminDeleteEmployee, adminUpdateEmployeePermissions, adminResetEmployeePassword,
  adminGetEmployeeAgents,
} from "@/api";

const PERMISSION_LABELS = [
  { key: "view_customers",        label: "View Customers",       description: "Can see customer list and profiles" },
  { key: "view_applications",     label: "View Applications",    description: "Can see all loan applications" },
  { key: "approve_applications",  label: "Approve Applications", description: "Can approve or reject applications (requires View Applications)" },
  { key: "view_disbursements",    label: "View Disbursements",   description: "Can see disbursement list" },
  { key: "approve_disbursements", label: "Approve Disbursements",description: "Can approve and mark disbursements transferred" },
  { key: "verify_kyc",            label: "Verify KYC",           description: "Can approve or reject Aadhaar documents" },
  { key: "view_agents",           label: "View Agents",          description: "Can see agent list and referral stats" },
  { key: "view_reports",          label: "View Reports",         description: "Can access reports section" },
  { key: "view_commissions",      label: "View Commissions",     description: "Can see commission records" },
];

const DEFAULT_PERMS = {
  view_customers: true,
  view_applications: true,
  approve_applications: false,
  view_disbursements: true,
  approve_disbursements: false,
  view_agents: true,
  view_reports: false,
  verify_kyc: true,
  view_commissions: false,
};

function PermissionToggle({ label, description, checked, onChange, disabled }) {
  return (
    <label className={`flex items-start justify-between gap-3 px-3 py-2.5 rounded-lg border border-slate-200 bg-slate-50/50 ${disabled ? 'opacity-60' : 'hover:bg-white cursor-pointer'}`}>
      <div className="flex-1 min-w-0">
        <span className="block text-sm font-semibold text-slate-700">{label}</span>
        {description && (
          <span className="block text-[11px] text-slate-500 mt-0.5 leading-snug">{description}</span>
        )}
      </div>
      <button
        type="button"
        disabled={disabled}
        role="switch"
        aria-checked={checked}
        onClick={() => !disabled && onChange(!checked)}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${checked ? 'bg-emerald-500' : 'bg-slate-300'}`}
      >
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
      </button>
    </label>
  );
}

function EmployeeFormModal({ mode, employee, onClose, onSaved }) {
  const isEdit = mode === 'edit';
  const [form, setForm] = useState({
    name: employee?.name || '',
    email: employee?.email || '',
    employee_id: employee?.employee_id || '',
    password: '',
  });
  const [permissions, setPermissions] = useState(employee?.permissions || { ...DEFAULT_PERMS });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.employee_id.trim()) {
      toast.error("Name, email and Employee ID are required");
      return;
    }
    if (!isEdit && form.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        // Two separate endpoints: basic fields + permissions
        await adminUpdateEmployee(employee.id, {
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
        });
        await adminUpdateEmployeePermissions(employee.id, permissions);
        toast.success("Employee updated");
      } else {
        await adminCreateEmployee({
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          employee_id: form.employee_id.trim(),
          password: form.password,
          permissions,
        });
        toast.success("Employee created");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <h3 className="text-lg font-black text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {isEdit ? `Edit ${employee?.name}` : 'Create Employee'}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1 block">Full Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Rohit Sharma" data-testid="emp-name-input" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1 block">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="rohit@rebuilt.in" data-testid="emp-email-input" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1 block">Employee ID</Label>
              <Input disabled={isEdit} value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value.toUpperCase() })} placeholder="EMP001" data-testid="emp-id-input" />
            </div>
            {!isEdit && (
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1 block">Initial Password</Label>
                <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min 6 chars" data-testid="emp-password-input" />
              </div>
            )}
          </div>

          <div>
            <h4 className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2 flex items-center gap-2">
              <Shield className="h-3.5 w-3.5" /> Permissions
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {PERMISSION_LABELS.map((p) => (
                <PermissionToggle
                  key={p.key}
                  label={p.label}
                  description={p.description}
                  checked={!!permissions[p.key]}
                  onChange={(v) => setPermissions({ ...permissions, [p.key]: v })}
                />
              ))}
            </div>
          </div>

          <div className="flex gap-3 justify-end pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-[#1565C0] hover:bg-[#0D47A1] text-white" data-testid="emp-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {isEdit ? 'Save Changes' : 'Create Employee'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ResetPasswordModal({ employee, onClose }) {
  const [pwd, setPwd] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (pwd.length < 6) { toast.error("Password must be at least 6 chars"); return; }
    setSaving(true);
    try {
      await adminResetEmployeePassword(employee.id, pwd);
      toast.success(`Password reset for ${employee.name}`);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-black text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Reset password
        </h3>
        <p className="text-xs text-slate-500 mb-4">
          Setting a new password for <span className="font-semibold">{employee.name}</span> ({employee.employee_id}).
        </p>
        <Input type="password" placeholder="New password (min 6 chars)" value={pwd} onChange={(e) => setPwd(e.target.value)} autoFocus data-testid="reset-pwd-input" />
        <div className="flex gap-3 justify-end mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={saving || pwd.length < 6} onClick={submit} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="reset-pwd-confirm">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <KeyRound className="h-4 w-4 mr-2" />}
            Reset Password
          </Button>
        </div>
      </div>
    </div>
  );
}

function ViewAgentsModal({ employee, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const res = await adminGetEmployeeAgents(employee.id);
        setData(res.data);
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Failed to load agents');
        onClose();
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee.id]);

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  const statusClass = (s) => s === 'approved'
    ? 'bg-emerald-100 text-emerald-700'
    : s === 'pending'
    ? 'bg-amber-100 text-amber-700'
    : s === 'rejected'
    ? 'bg-red-100 text-red-700'
    : 'bg-slate-100 text-slate-600';

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="view-agents-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h3 className="text-xl font-black text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Agents onboarded by {employee.name}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {employee.employee_id} · code <code className="font-mono text-violet-700">{employee.employee_referral_code || 'n/a'}</code>
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100" aria-label="Close"><X className="h-5 w-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="py-20 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#1565C0]" /></div>
          ) : !data || data.total === 0 ? (
            <div className="py-20 text-center text-slate-500" data-testid="view-agents-empty">
              <UsersIcon className="h-12 w-12 mx-auto text-slate-300 mb-3" />
              <p className="text-sm">No agents have registered using this employee's referral code yet.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-4 gap-2 p-4 bg-slate-50 border-b text-center text-xs">
                <div className="p-2 rounded-lg bg-white border border-slate-200"><div className="font-black text-xl">{data.total}</div><div className="text-slate-500">Total</div></div>
                <div className="p-2 rounded-lg bg-white border border-emerald-200"><div className="font-black text-xl text-emerald-700">{data.approved_count}</div><div className="text-slate-500">Approved</div></div>
                <div className="p-2 rounded-lg bg-white border border-amber-200"><div className="font-black text-xl text-amber-700">{data.pending_count}</div><div className="text-slate-500">Pending</div></div>
                <div className="p-2 rounded-lg bg-white border border-red-200"><div className="font-black text-xl text-red-700">{data.rejected_count}</div><div className="text-slate-500">Rejected</div></div>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 font-bold">
                  <tr>
                    <th className="text-left px-5 py-3">Name</th>
                    <th className="text-left px-5 py-3">Mobile</th>
                    <th className="text-left px-5 py-3">Status</th>
                    <th className="text-left px-5 py-3">Referrals</th>
                    <th className="text-left px-5 py-3">Joined</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.agents.map((a) => (
                    <tr key={a.id} data-testid={`onboarded-agent-${a.id}`}>
                      <td className="px-5 py-3 font-semibold text-slate-800">{a.name}</td>
                      <td className="px-5 py-3 text-slate-600 font-mono">{a.phone}</td>
                      <td className="px-5 py-3">
                        <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full ${statusClass(a.application_status)}`}>
                          {a.application_status}
                        </span>
                      </td>
                      <td className="px-5 py-3 font-semibold text-slate-700">{a.total_referrals || 0}</td>
                      <td className="px-5 py-3 text-slate-500 text-xs">{fmtDate(a.created_at || a.applied_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminEmployees() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);     // employee | null
  const [creating, setCreating] = useState(false);
  const [resetting, setResetting] = useState(null); // employee | null
  const [viewingAgents, setViewingAgents] = useState(null); // employee | null

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminListEmployees();
      setEmployees(res.data.employees || []);
    } catch (e) {
      toast.error("Failed to load employees");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = employees.filter((e) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (e.name || '').toLowerCase().includes(s)
      || (e.email || '').toLowerCase().includes(s)
      || (e.employee_id || '').toLowerCase().includes(s);
  });

  const toggleStatus = async (emp) => {
    const next = emp.status === 'active' ? 'inactive' : 'active';
    if (!window.confirm(`${next === 'active' ? 'Reactivate' : 'Deactivate'} ${emp.name}?`)) return;
    try {
      if (next === 'inactive') {
        await adminDeleteEmployee(emp.id);
      } else {
        await adminUpdateEmployee(emp.id, { status: 'active' });
      }
      toast.success(next === 'active' ? "Reactivated" : "Deactivated");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const countPerms = (perms) => Object.values(perms || {}).filter(Boolean).length;
  const formatDate = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-3xl font-black text-[#0A2540] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Employees
          </h2>
          <p className="text-sm text-[#6B7280] mt-1">
            Create and manage staff accounts. Each employee has fine-grained permissions.
          </p>
        </div>
        <Button
          onClick={() => setCreating(true)}
          className="bg-gradient-to-r from-[#1565C0] to-[#0D47A1] text-white rounded-xl px-5 py-2.5 font-semibold shadow-sm hover:shadow-md transition-all"
          data-testid="create-employee-btn"
        >
          <Plus className="h-4 w-4 mr-2" /> Create Employee
        </Button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by name, email or Employee ID…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 rounded-xl border-slate-200"
            data-testid="emp-search"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-16 flex items-center justify-center">
            <Loader2 className="h-8 w-8 text-[#1565C0] animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center" data-testid="emp-empty">
            <UserCog className="h-12 w-12 mx-auto text-slate-300 mb-3" />
            <p className="text-sm text-slate-500">
              {search ? 'No employees match your search.' : 'No employees yet. Create one above.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 font-bold">
                <tr>
                  <th className="text-left px-5 py-3">Name</th>
                  <th className="text-left px-5 py-3">Employee ID</th>
                  <th className="text-left px-5 py-3">Email</th>
                  <th className="text-left px-5 py-3">Status</th>
                  <th className="text-left px-5 py-3">Referral Code</th>
                  <th className="text-left px-5 py-3">Agents Referred</th>
                  <th className="text-left px-5 py-3">Permissions</th>
                  <th className="text-left px-5 py-3">Last Login</th>
                  <th className="text-right px-5 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((e) => (
                  <tr key={e.id} className="hover:bg-slate-50/60" data-testid={`emp-row-${e.employee_id}`}>
                    <td className="px-5 py-3 font-bold text-slate-900">{e.name}</td>
                    <td className="px-5 py-3 font-mono text-slate-700">{e.employee_id}</td>
                    <td className="px-5 py-3 text-slate-600 flex items-center gap-1.5"><Mail className="h-3 w-3 text-slate-400" /> {e.email}</td>
                    <td className="px-5 py-3">
                      {e.status === 'active' ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                          <CheckCircle2 className="h-3 w-3" /> Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full">
                          <XCircle className="h-3 w-3" /> Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      {e.employee_referral_code ? (
                        <code className="bg-violet-50 text-violet-700 border border-violet-100 px-2 py-1 rounded-md text-[11px] font-mono font-bold" data-testid={`emp-ref-code-${e.employee_id}`}>
                          {e.employee_referral_code}
                        </code>
                      ) : (
                        <span className="text-[11px] text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <span className="inline-flex items-center gap-1 text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-100 px-2 py-0.5 rounded-full" data-testid={`emp-agents-referred-${e.employee_id}`}>
                        <UserCog className="h-3 w-3" /> {e.total_agents_referred || 0}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <span className="inline-flex items-center gap-1 text-[11px] font-bold bg-blue-100 text-[#1565C0] px-2 py-0.5 rounded-full">
                        <Shield className="h-3 w-3" /> {countPerms(e.permissions)} / {PERMISSION_LABELS.length}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-[11px] text-slate-500 flex items-center gap-1"><Clock className="h-3 w-3" /> {formatDate(e.last_login)}</td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setViewingAgents(e)}
                          className="p-2 rounded-lg text-[#1565C0] hover:bg-blue-50"
                          title="View agents onboarded by this employee"
                          data-testid={`view-agents-${e.employee_id}`}
                        >
                          <UsersIcon className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => setEditing(e)}
                          className="p-2 rounded-lg text-slate-600 hover:bg-slate-100"
                          title="Edit"
                          data-testid={`edit-${e.employee_id}`}
                        >
                          <Edit className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => setResetting(e)}
                          className="p-2 rounded-lg text-amber-600 hover:bg-amber-50"
                          title="Reset Password"
                          data-testid={`reset-${e.employee_id}`}
                        >
                          <KeyRound className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => toggleStatus(e)}
                          className={`p-2 rounded-lg ${e.status === 'active' ? 'text-red-600 hover:bg-red-50' : 'text-emerald-600 hover:bg-emerald-50'}`}
                          title={e.status === 'active' ? 'Deactivate' : 'Reactivate'}
                          data-testid={`toggle-${e.employee_id}`}
                        >
                          {e.status === 'active' ? <UserX className="h-3.5 w-3.5" /> : <UserCheck className="h-3.5 w-3.5" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {creating && <EmployeeFormModal mode="create" onClose={() => setCreating(false)} onSaved={load} />}
      {editing && <EmployeeFormModal mode="edit" employee={editing} onClose={() => setEditing(null)} onSaved={load} />}
      {resetting && <ResetPasswordModal employee={resetting} onClose={() => setResetting(null)} />}
      {viewingAgents && <ViewAgentsModal employee={viewingAgents} onClose={() => setViewingAgents(null)} />}
    </div>
  );
}
